import React from 'react';

import { Tabs } from 'antd';
import styled from 'styled-components';

import { PendingTaskBreakdownType, TaskControlActionType, TaskQueueStatusType } from 'types/tasks';

import ActiveTasksTab from './ActiveTasksTab';
import { TASK_QUEUE_TAB_KEYS } from './constants';
import PendingTasksTab from './PendingTasksTab';
import RecentFailuresTab from './RecentFailuresTab';
import TaskControlsTab from './TaskControlsTab';

const Panel = styled.section`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  padding: 20px;
  overflow: hidden;
  border: 1px solid var(--line-gray);
  border-radius: 10px;
  background: var(--white);
  box-shadow: 0 2px 8px var(--box-shadow-gray-dark);
`;

const StyledTabs = styled(Tabs)`
  min-height: 0;
  flex: 1;

  .ant-tabs-nav {
    margin-bottom: 20px;
  }

  .ant-tabs-content-holder {
    min-height: 0;
    overflow: hidden;
  }

  .ant-tabs-content,
  .ant-tabs-tabpane {
    height: 100%;
  }

  .ant-tabs-tab-btn {
    color: var(--text-secondary);
    font-family: var(--font-bold);
  }

  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: var(--active-blue);
  }

  .ant-tabs-ink-bar {
    background: var(--active-blue);
  }
`;

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
`;

const TabCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  padding: 2px 6px;
  border-radius: 10px;
  color: var(--text-secondary);
  background: var(--lighter-gray);
  font-family: var(--font-regular);
  font-size: 11px;
`;

const getTabLabel = (label: string, count: number) => (
  <TabLabel>
    {label} <TabCount>{count}</TabCount>
  </TabLabel>
);

type Props = {
  status?: TaskQueueStatusType;
  breakdown?: PendingTaskBreakdownType;
  isStatusLoading: boolean;
  isBreakdownLoading: boolean;
  hasBreakdownError: boolean;
  isTaskControlUpdating: boolean;
  onChangeTaskRevocation: (taskName: string, action: TaskControlActionType) => Promise<void>;
};

const TaskQueueTabs = ({
  status,
  breakdown,
  isStatusLoading,
  isBreakdownLoading,
  hasBreakdownError,
  isTaskControlUpdating,
  onChangeTaskRevocation,
}: Props) => {
  const items = [
    {
      key: TASK_QUEUE_TAB_KEYS.ACTIVE,
      label: getTabLabel('Active tasks', status?.executing_count ?? 0),
      children: <ActiveTasksTab tasks={status?.active_tasks ?? []} isLoading={isStatusLoading} />,
    },
    {
      key: TASK_QUEUE_TAB_KEYS.PENDING,
      label: getTabLabel('Pending breakdown', breakdown?.total ?? 0),
      children: <PendingTasksTab breakdown={breakdown} isLoading={isBreakdownLoading} hasError={hasBreakdownError} />,
    },
    {
      key: TASK_QUEUE_TAB_KEYS.CONTROLS,
      label: getTabLabel('Task controls', status?.registered_tasks?.length ?? 0),
      children: (
        <TaskControlsTab
          canManageTasks={status?.can_manage_tasks ?? false}
          isLoading={isStatusLoading}
          isUpdating={isTaskControlUpdating}
          onChangeRevocation={onChangeTaskRevocation}
          tasks={status?.registered_tasks ?? []}
        />
      ),
    },
    {
      key: TASK_QUEUE_TAB_KEYS.FAILURES,
      label: getTabLabel('Recent failures', status?.recent_failures.length ?? 0),
      children: <RecentFailuresTab tasks={status?.recent_failures ?? []} isLoading={isStatusLoading} />,
    },
  ];

  return (
    <Panel>
      <StyledTabs defaultActiveKey={TASK_QUEUE_TAB_KEYS.ACTIVE} items={items} />
    </Panel>
  );
};

export default TaskQueueTabs;
